import { useState } from "react";
import { Icon } from "@astrum/ui";
import { MAX_CUSTOM_FILTERS } from "@astrum/shared";
import type { CustomField, CustomFilter, CustomFilterOp } from "@astrum/shared";
import { FILTER_OP_LABELS, filterValues, opsFor } from "../../lib/custom-fields.js";
import "./task-views.css";

interface Props {
  fields: CustomField[];
  filters: CustomFilter[];
  people: { id: string; displayName: string }[];
  onChange: (filters: CustomFilter[]) => void;
}

/**
 * Filters on a project's custom fields: field, operator, value per row, all
 * combined with AND. A dropdown or person filter is "is any of" and takes a
 * set; the others take one typed value.
 */
export function CustomFieldFilters({ fields, filters, people, onChange }: Props) {
  const [open, setOpen] = useState(false);
  if (fields.length === 0) return null;

  const patch = (index: number, change: Partial<CustomFilter>) =>
    onChange(filters.map((f, i) => (i === index ? { ...f, ...change } : f)));
  const add = () => {
    const field = fields[0]!;
    onChange([...filters, { fieldId: field.id, op: opsFor(field.type)[0]!, value: "" }]);
  };

  return (
    <div className="tv-popover">
      <button type="button" className="tv-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Icon name="filter" size={13} /> Fields{filters.length ? ` (${filters.length})` : ""}
      </button>
      {open && (
        <div className="tv-popover__panel tv-cfilters" role="group" aria-label="Filter by custom fields">
          {filters.map((filter, index) => {
            const field = fields.find((f) => f.id === filter.fieldId);
            return (
              <div className="tv-cfilters__row" key={index}>
                <select
                  className="tv-select"
                  aria-label="Field"
                  value={filter.fieldId}
                  onChange={(e) => {
                    const next = fields.find((f) => f.id === e.target.value)!;
                    patch(index, { fieldId: next.id, op: opsFor(next.type)[0]!, value: "" });
                  }}
                >
                  {!field && <option value={filter.fieldId}>Deleted field</option>}
                  {fields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <select
                  className="tv-select"
                  aria-label="Operator"
                  value={filter.op}
                  onChange={(e) => patch(index, { op: e.target.value as CustomFilterOp })}
                >
                  {(field ? opsFor(field.type) : [filter.op]).map((op) => (
                    <option key={op} value={op}>
                      {field?.type === "date" && op === "gte" ? "on or after" : field?.type === "date" && op === "lte" ? "on or before" : FILTER_OP_LABELS[op]}
                    </option>
                  ))}
                </select>
                <ValueInput field={field} filter={filter} people={people} onChange={(value) => patch(index, { value })} />
                <button type="button" className="tv-link" aria-label="Remove filter" onClick={() => onChange(filters.filter((_, i) => i !== index))}>
                  <Icon name="close" size={12} />
                </button>
              </div>
            );
          })}
          {filters.length < MAX_CUSTOM_FILTERS && (
            <button type="button" className="tv-link" onClick={add}>
              Add field filter
            </button>
          )}
          {filters.length > 0 && (
            <button type="button" className="tv-link" onClick={() => onChange([])}>
              Clear field filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ValueInput({
  field,
  filter,
  people,
  onChange,
}: {
  field: CustomField | undefined;
  filter: CustomFilter;
  people: Props["people"];
  onChange: (value: string) => void;
}) {
  const choices =
    field?.type === "dropdown"
      ? field.options.map((o) => ({ id: o.id, label: o.label }))
      : field?.type === "person"
        ? people.map((p) => ({ id: p.id, label: p.displayName }))
        : null;
  if (choices) {
    const picked = filterValues(filter);
    return (
      <div className="tv-cfilters__choices" role="group" aria-label="Values">
        {choices.map((c) => (
          <label key={c.id} className="tv-toolbar__field">
            <input
              type="checkbox"
              checked={picked.includes(c.id)}
              onChange={(e) => onChange((e.target.checked ? [...picked, c.id] : picked.filter((id) => id !== c.id)).join(","))}
            />
            {c.label}
          </label>
        ))}
      </div>
    );
  }
  return (
    <input
      className="tv-input"
      aria-label="Value"
      type={field?.type === "date" ? "date" : "text"}
      inputMode={field?.type === "number" ? "decimal" : undefined}
      value={filter.value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
